/* eslint-disable react/no-unstable-nested-components */
import { FC, useMemo, useState } from 'react';
// Input.Search has no in-app equivalent — keep antd for it. notification is
// antd's imperative API and has no Base wrapper.
import { Input, Space, notification } from 'antd';
import dayjs from 'dayjs';
import Button from 'components/Base/Button/Button';
import { Select } from 'components/Base/Select';
import { EmptyView } from 'components/Base/EmptyView';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import {
  ETableStoragePaths,
  TColumnType,
} from 'components/Base/Table/Table.types';
import CardContent from 'components/Base/Layout/Content/CardContent';
import {
  EDuplicateEntityType,
  EMergeStatus,
  ERestoreOutcomeStatus,
  TMergeHistoryEntryUI,
} from 'data/types/duplicateManagement.types';
import { EUserType } from 'data/types/user.types';
import { useAppSelector } from 'redux/store';
import {
  useRestoreMergeMutation,
  useSearchMergeHistoryQuery,
} from 'redux/apiSlice/duplicateManagement.slice';
import {
  SExpandedRow,
  SStatusBadge,
  SToolbar,
} from '../../DuplicateMerge.styles';
import RestoreConfirmModal from '../../components/RestoreConfirmModal';
import FieldDeltasView from '../../components/FieldDeltasView';

const DEFAULT_PAGE_SIZE = 10;

// BE retention window for merge restoration. Used as a client-side hint only —
// the BE re-checks and returns FAILED_PAST_RETENTION if exceeded.
const RESTORE_WINDOW_DAYS = 30;

const ENTITY_FILTER_OPTIONS: {
  value: EDuplicateEntityType | 'ALL';
  label: string;
}[] = [
  { value: 'ALL', label: 'All entities' },
  { value: EDuplicateEntityType.PEOPLE, label: 'People' },
  { value: EDuplicateEntityType.ORGANIZATION, label: 'Organization' },
];

const statusBadge = (status: EMergeStatus) => {
  const cfg: Partial<Record<EMergeStatus, { label: string; tone: string }>> = {
    [EMergeStatus.SUCCESS]: { label: 'Merged', tone: 'green' },
    [EMergeStatus.FAILED]: { label: 'Failed', tone: 'red' },
    [EMergeStatus.RESTORED]: { label: 'Restored', tone: 'blue' },
    [EMergeStatus.SIMULATED]: { label: 'Simulated', tone: 'gray' },
    [EMergeStatus.IN_PROGRESS]: { label: 'In progress', tone: 'yellow' },
  };
  const c = cfg[status];
  if (!c) return null;
  return <SStatusBadge $tone={c.tone}>{c.label}</SStatusBadge>;
};

const entityLabel = (t: EDuplicateEntityType) =>
  t === EDuplicateEntityType.PEOPLE ? 'People' : 'Organization';

const HistoryTab: FC = () => {
  const user = useAppSelector(state => state.userSlice?.user);
  const isSuperAdmin = user?.type?.includes(EUserType.SUPER_ADMIN) ?? false;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [entityFilter, setEntityFilter] = useState<
    EDuplicateEntityType | 'ALL'
  >('ALL');
  const [initiatedByQuery, setInitiatedByQuery] = useState('');
  const [restoreTargetId, setRestoreTargetId] = useState<string | null>(null);

  const { data, isFetching } = useSearchMergeHistoryQuery({
    page,
    size: pageSize,
    ...(entityFilter !== 'ALL' ? { entityType: entityFilter } : {}),
    ...(initiatedByQuery ? { initiatedBy: initiatedByQuery } : {}),
    sortBy: 'initiatedAt',
    sortDirection: 'desc',
  });

  const [restoreMerge, { isLoading: restoring }] = useRestoreMergeMutation();

  const targetEntry = useMemo(
    () => data?.mergeHistory.find(e => e.id === restoreTargetId) || null,
    [data?.mergeHistory, restoreTargetId],
  );

  const hasActiveFilters = entityFilter !== 'ALL' || initiatedByQuery !== '';

  const handleRestoreConfirm = async () => {
    if (!targetEntry) return;
    try {
      const response = await restoreMerge({
        mergeHistoryId: targetEntry.id,
      }).unwrap();

      switch (response.status) {
        case ERestoreOutcomeStatus.RESTORED:
          notification.success({
            message: 'Merge restored',
            description:
              response.restoredAt && response.restoredBy
                ? `Restored on ${dayjs(response.restoredAt).format(
                    'MMM D, YYYY',
                  )} by ${response.restoredBy}`
                : response.message || 'Merge restored successfully.',
          });
          break;
        case ERestoreOutcomeStatus.FAILED_PAST_RETENTION:
          notification.error({
            message: 'Restore failed',
            description: response.message || 'Restore window expired (30 days)',
          });
          break;
        case ERestoreOutcomeStatus.FAILED_INVALID_STATE:
          notification.error({
            message: 'Restore failed',
            description: response.message || 'Cannot restore this merge',
          });
          break;
        case ERestoreOutcomeStatus.FAILED_LEGACY_SCHEMA:
          notification.error({
            message: 'Restore failed',
            description:
              response.message || 'Pre-v2 merge — restore unavailable',
          });
          break;
        default:
          break;
      }

      if (response.restorationWarnings.length > 0) {
        notification.warning({
          message: 'Restore warnings',
          description: (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {response.restorationWarnings.map(w => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          ),
          duration: 0,
        });
      }
    } catch (err) {
      notification.error({
        message: 'Restore failed',
        description: 'Could not contact the server. Try again.',
      });
    } finally {
      setRestoreTargetId(null);
    }
  };

  // Restore button visible when SUPER_ADMIN, row is SUCCESS, and the merge is
  // inside the 30-day retention window. BE re-validates and may still reject.
  const canShowRestoreButton = (entry: TMergeHistoryEntryUI): boolean => {
    if (!isSuperAdmin) return false;
    if (entry.status !== EMergeStatus.SUCCESS) return false;
    const daysSinceMerge = dayjs().diff(dayjs(entry.initiatedAt), 'day');
    return daysSinceMerge <= RESTORE_WINDOW_DAYS;
  };

  const columns: TColumnType<TMergeHistoryEntryUI>[] = useMemo(
    () => [
      {
        title: 'Merged Date',
        key: 'initiatedAt',
        width: 160,
        render: (_v, row) => dayjs(row.initiatedAt).format('MMM D, YYYY'),
      },
      {
        title: 'Merged by',
        key: 'initiatedBy',
        dataIndex: 'initiatedBy',
        width: 220,
      },
      {
        title: 'Entity',
        key: 'entityType',
        width: 140,
        render: (_v, row) => entityLabel(row.entityType),
      },
      {
        title: 'Primary record',
        key: 'primaryRecordId',
        render: (_v, row) => row.primaryRecordId,
      },
      {
        title: 'Status',
        key: 'status',
        width: 200,
        render: (_v, row) => statusBadge(row.status),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_v, row) =>
          canShowRestoreButton(row) ? (
            <Button
              variant="default"
              size="small"
              onClick={() => setRestoreTargetId(row.id)}
            >
              Restore
            </Button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin],
  );

  const tableHeaderActions = (
    <Space key="filters">
      <Input.Search
        placeholder="Filter by initiator email…"
        allowClear
        defaultValue={initiatedByQuery}
        onSearch={value => {
          setInitiatedByQuery(value.trim());
          setPage(0);
        }}
        style={{ width: 260 }}
      />
      <Select
        value={entityFilter}
        onChange={value => {
          setEntityFilter(value as EDuplicateEntityType | 'ALL');
          setPage(0);
        }}
        options={ENTITY_FILTER_OPTIONS}
        style={{ width: 180 }}
      />
    </Space>
  );

  return (
    <>
      <CardContent>
        <SToolbar>{tableHeaderActions}</SToolbar>
        <Table<TMergeHistoryEntryUI>
          rowKey="id"
          columns={columns}
          dataSource={data?.mergeHistory || []}
          loading={isFetching}
          currentPage={page}
          pageSize={pageSize}
          setPageSize={size => {
            setPageSize(size);
            setPage(0);
          }}
          totalDataCount={data?.totalElements || 0}
          onChange={pagination => {
            if (pagination.current && pagination.current - 1 !== page) {
              setPage(pagination.current - 1);
            }
          }}
          storagePath={ETableStoragePaths.DUPLICATE_MERGE_HISTORY}
          hideActions
          tabHeight={44}
          locale={{
            emptyText: (
              <EmptyView
                alt="no history"
                emptyText={
                  hasActiveFilters
                    ? 'No merge history matches the current filters.'
                    : 'No merges recorded yet.'
                }
                icon={EIconNames.EMPTY_DATA}
              />
            ),
          }}
          expandable={{
            expandedRowRender: row => (
              <SExpandedRow>
                <FieldDeltasView entry={row} />
              </SExpandedRow>
            ),
          }}
        />
      </CardContent>

      <RestoreConfirmModal
        open={!!restoreTargetId}
        loading={restoring}
        onCancel={() => setRestoreTargetId(null)}
        onConfirm={handleRestoreConfirm}
      />
    </>
  );
};

export default HistoryTab;
